import type { BookTypeEng } from "@/types/acquirebook";
import type {
  GeoSpaceChiban,
  MarkerState,
  PropertySelectionRow,
} from "../../types";
import { normalizeChibanKaokuNumber, normalizeLocation } from "../index";
import { generateNextId } from "./generateNextId";

/**
 * RealEstateType の ID を登記取得用の BookTypeEng に変換する。
 * 建物・土地・区分建物・一棟のみが対象。
 */
export const convertRealEstateTypeIdToBookType = (
  realEstateTypeId: number
): BookTypeEng | "NOT_TARGET" => {
  switch (realEstateTypeId) {
    case 1:
      return "BUILDING";
    case 2:
      return "LAND";
    case 3:
      return "CONDOMINIUM";
    case 6:
      return "ONE_BUILDING";
    default:
      return "NOT_TARGET";
  }
};

/**
 * 地番情報から所在文字列を構築する（city + ooaza + aza）
 */
export const buildLocationStringFromChiban = (r: GeoSpaceChiban): string => {
  const ooazaName = r.ooazaName ?? "";
  const azaName = r.azaName ?? "";
  return `${r.cityName}${ooazaName}${azaName}`;
};

/**
 * 受付帳マーカーからリスト行を組み立てる。
 * book / chibanRawData が無い、または対象外種別の場合は null。
 */
export const buildReceptionBookPropertyRowFromMarker = (
  marker: MarkerState,
  nextId: number,
  buildLocationString: (
    r: GeoSpaceChiban
  ) => string = buildLocationStringFromChiban
): PropertySelectionRow | null => {
  if (!marker.chibanRawData || !marker.book) {
    return null;
  }

  const bookType = convertRealEstateTypeIdToBookType(
    marker.book.realEstateType
  );
  if (bookType === "NOT_TARGET") {
    return null;
  }

  const chiban = marker.chibanRawData;
  return {
    id: nextId,
    selected: true,
    bookType,
    prefName: chiban.prefName,
    location: normalizeLocation(buildLocationString(chiban)),
    chibanKaokuNumber: normalizeChibanKaokuNumber(chiban.chiban),
    area: chiban.area,
    isFeedOrigin: true,
    rawData: chiban,
    tags: [],
    remarks: "",
    pinType: "reception-book",
    bookId: marker.book.id,
  };
};

type FindDuplicatePropertyIndex = (
  rows: PropertySelectionRow[],
  bookType: BookTypeEng,
  prefName: string,
  location: string,
  chiban: string
) => number;

type MergePropertySelectionRow = (
  rows: PropertySelectionRow[],
  index: number,
  area: number | null,
  rawData?: GeoSpaceChiban
) => PropertySelectionRow[] | undefined;

export interface EnsureReceptionBookPropertyRowParams {
  marker: MarkerState;
  rows: PropertySelectionRow[];
  findDuplicatePropertyIndex: FindDuplicatePropertyIndex;
  mergePropertySelectionRow: MergePropertySelectionRow;
  /** 左下の物件選択リストへ追加するか。無料ユーザーは false。 */
  addToList: boolean;
  buildLocationString?: (r: GeoSpaceChiban) => string;
}

/**
 * 受付帳マーカーに対応するリスト行が無ければ追加する。
 * 既にあれば（必要なら area / rawData を merge して）既存配列を返す。
 */
export const ensureReceptionBookPropertyRow = ({
  marker,
  rows,
  findDuplicatePropertyIndex,
  mergePropertySelectionRow,
  addToList,
  buildLocationString = buildLocationStringFromChiban,
}: EnsureReceptionBookPropertyRowParams): PropertySelectionRow[] => {
  if (!addToList) {
    return rows;
  }

  const candidate = buildReceptionBookPropertyRowFromMarker(
    marker,
    0,
    buildLocationString
  );
  if (!candidate) {
    return rows;
  }

  const duplicateIndex = findDuplicatePropertyIndex(
    rows,
    candidate.bookType,
    candidate.prefName,
    candidate.location,
    candidate.chibanKaokuNumber
  );

  if (duplicateIndex !== -1) {
    const merged = mergePropertySelectionRow(
      rows,
      duplicateIndex,
      candidate.area,
      candidate.rawData
    );
    return merged ?? rows;
  }

  return [...rows, { ...candidate, id: generateNextId(rows) }];
};

/**
 * 受付帳マーカーに対応するリスト行を、bookId + 所在・地番で特定する。
 * 配列インデックス依存を避けるために ReceptionBookLayer から利用する。
 */
export const findReceptionBookRowForMarker = (
  receptionBookRows: PropertySelectionRow[],
  marker: MarkerState,
  buildLocationString: (
    r: GeoSpaceChiban
  ) => string = buildLocationStringFromChiban
): PropertySelectionRow | undefined => {
  if (!marker.book || !marker.chibanRawData) {
    return undefined;
  }

  const location = normalizeLocation(buildLocationString(marker.chibanRawData));
  const chiban = normalizeChibanKaokuNumber(marker.chibanRawData.chiban);
  const bookId = marker.book.id;

  return receptionBookRows.find(
    (prop) =>
      prop.bookId === bookId &&
      normalizeLocation(prop.location) === location &&
      normalizeChibanKaokuNumber(prop.chibanKaokuNumber) === chiban
  );
};
